/**
 * Centralized file upload configuration - single source of truth.
 * Used by both frontend and backend for consistent validation.
 */

// ---------------------------------------------------------------------------
// Allowed MIME types → extensions
// ---------------------------------------------------------------------------
export const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  // Images
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/svg+xml': ['.svg'],
  'image/x-icon': ['.ico'],
  'image/vnd.microsoft.icon': ['.ico'],

  // Fonts
  'font/otf': ['.otf'],
  'application/vnd.ms-opentype': ['.otf'],
  'font/ttf': ['.ttf'],
  'application/x-font-ttf': ['.ttf'],
  'font/woff': ['.woff'],
  'application/font-woff': ['.woff'],
  'font/woff2': ['.woff2'],

  // Documents
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'text/html': ['.html', '.htm'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/rtf': ['.rtf'],

  // Presentations
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],

  // Data
  'application/json': ['.json'],
  'text/csv': ['.csv'],
  'text/tab-separated-values': ['.tsv'],
  'text/xml': ['.xml'],
  'application/xml': ['.xml'],
  'text/yaml': ['.yaml', '.yml'],
  'application/x-yaml': ['.yaml', '.yml'],

  // Spreadsheets
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],

  // Archives
  'application/zip': ['.zip'],
  'application/gzip': ['.gz'],
  'application/x-tar': ['.tar'],
  'application/x-7z-compressed': ['.7z'],
  'application/x-rar-compressed': ['.rar'],

  // Audio
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/ogg': ['.ogg'],
  'audio/aac': ['.aac'],
  'audio/flac': ['.flac'],
  'audio/mp4': ['.m4a'],
  'audio/webm': ['.weba'],

  // Video
  'video/mp4': ['.mp4'],
  'video/webm': ['.webm'],
  'video/ogg': ['.ogv'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],

  // Code / web
  'text/css': ['.css'],
  'application/wasm': ['.wasm'],
};

// ---------------------------------------------------------------------------
// Blocked extensions - dangerous file types that must never be uploaded
// ---------------------------------------------------------------------------
export const BLOCKED_EXTENSIONS: Set<string> = new Set([
  // Executables
  '.exe', '.dll', '.bat', '.cmd', '.com', '.msi', '.scr', '.cpl',
  // Scripts
  '.ps1', '.sh', '.bash', '.vbs', '.js', '.py', '.rb', '.php', '.jsp', '.asp', '.aspx',
  '.ws', '.wsf',
  // Java
  '.jar',
  // Windows config
  '.inf', '.reg',
  // Office macros
  '.docm', '.xlsm', '.pptm',
]);

// ---------------------------------------------------------------------------
// Max file size
// ---------------------------------------------------------------------------
export const MAX_FILE_SIZE_MB = 50;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// ---------------------------------------------------------------------------
// HTML accept attribute (array form for Uppy, string form for <input>)
// ---------------------------------------------------------------------------

/** Build a deduplicated set of extensions from the MIME map */
function buildUniqueExtensions(): string[] {
  const exts = new Set<string>();
  for (const extensions of Object.values(ALLOWED_MIME_TYPES)) {
    for (const ext of extensions) {
      exts.add(ext);
    }
  }
  return Array.from(exts).sort();
}

/**
 * Array of allowed file type specifiers for Uppy `allowedFileTypes`.
 * Includes wildcard groups (image/*, video/*, audio/*) plus individual extensions.
 */
export const ACCEPT_ARRAY: string[] = [
  'image/*',
  'video/*',
  'audio/*',
  ...buildUniqueExtensions(),
];

/**
 * Comma-separated string for HTML `<input accept="...">`.
 */
export const ACCEPT_ATTRIBUTE: string = ACCEPT_ARRAY.join(',');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Check if a filename has a blocked (dangerous) extension.
 * Matches against the final extension, case-insensitive.
 */
export function isBlockedExtension(filename: string): boolean {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === 0) return false;
  const ext = filename.substring(lastDot).toLowerCase();
  return BLOCKED_EXTENSIONS.has(ext);
}
